import { useEffect, useState } from "react";
import type { Server, Position, Address } from "../data/types";
import { localizeServerName, t } from "../utils/i18n";
import { getMockIconUrl, isGreekName } from "../utils/serverIcons";
import { AddressSelector } from "./AddressSelector";
import { CardPool } from "./CardPool";
import { CustomScreen } from "./CustomScreen";
import { PositionSelector } from "./PositionSelector";

interface Props {
  server: Server;
}

export function ServerView({ server }: Props) {
  const [position, setPosition] = useState<Position>(1);
  const [address, setAddress] = useState<Address>("A");

  // 名前がギリシャ文字（アルファ、ミューなど）の場合はモックを優先する
  const useMockPriority = isGreekName(server.name);
  const mockUrl = getMockIconUrl(server.name, server.type);
  const primarySrc = useMockPriority
    ? mockUrl
    : (server.v3ImagePath ?? server.imagePath);

  return (
    <main className="flex h-full flex-1 flex-col overflow-y-auto p-3 sm:p-4">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-3 sm:gap-4">
        <header className="flex items-center gap-3">
          <ServerImage
            src={primarySrc}
            alt={localizeServerName(server.name)}
            pixelated={!useMockPriority && server.v3ImagePath !== null}
            fallbackSrc={useMockPriority ? (server.v3ImagePath ?? server.imagePath) : mockUrl}
          />
          <div>
            <h1 className="text-lg font-medium leading-tight sm:text-xl">
              Lv{server.level} {localizeServerName(server.name)}
            </h1>
            <p className="text-xs text-slate-500">
              {server.type === 0 ? t("typeSatellite") : t("typeMeteor")}
            </p>
          </div>
        </header>

        <div className="flex flex-col items-center gap-3 sm:gap-4">
          <PositionSelector value={position} onChange={setPosition} />
          <AddressSelector value={address} onChange={setAddress} />
        </div>

        <div className="flex flex-wrap items-start justify-center gap-4 sm:gap-6">
          <CustomScreen
            cards={server.cards}
            position={position}
            address={address}
            spriteSrc={server.imagePath}
          />
          <CardPool
            cards={server.cards}
            position={position}
            address={address}
            spriteSrc={server.imagePath}
          />
        </div>
      </div>
    </main>
  );
}

function ServerImage({
  src,
  alt,
  pixelated = false,
  fallbackSrc,
}: {
  src: string;
  alt: string;
  pixelated?: boolean;
  fallbackSrc: string;
}) {
  const [currentSrc, setCurrentSrc] = useState(src);

  useEffect(() => {
    setCurrentSrc(src);
  }, [src]);

  return (
    <img
      src={currentSrc}
      alt={alt}
      className="h-12 w-12 rounded border border-slate-300 bg-white object-contain sm:h-16 sm:w-16"
      style={pixelated ? { imageRendering: "pixelated" } : undefined}
      onError={() => {
        if (currentSrc !== fallbackSrc) {
          setCurrentSrc(fallbackSrc);
        }
      }}
    />
  );
}
